import { useRef, type ReactNode } from 'react';

import { cx } from './cx';

/**
 * The view switch, and the report's view switch: a well in `surface-2` with the current
 * segment lifted onto `surface` under a 1px shadow.
 *
 * A radio group rather than a row of buttons, so that arrow keys move between the views and
 * the reading of it is "Desk, one of three" rather than three unrelated buttons.
 */
export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
  className,
  tabbable = true,
  compact = false,
}: {
  label: string;
  value: T;
  options: { value: T; label: ReactNode; title?: string; marked?: boolean }[];
  onChange: (next: T) => void;
  className?: string;
  /**
   * False to keep it out of the Tab order, for a switch beside a field that is filled by
   * tabbing through a form and has a shortcut of its own — the address search's (#382).
   */
  tabbable?: boolean;
  /** Tighter segments, for a switch sharing a narrow card's row with its label. */
  compact?: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={box}
      role="radiogroup"
      aria-label={label}
      className={cx(
        'inline-flex bg-surface-2 border border-rule rounded-card p-0.5 gap-0.5',
        className,
      )}
      onKeyDown={(event) => {
        const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;

        if (!step) return;

        event.preventDefault();

        const at = options.findIndex((option) => option.value === value);
        const next = options[(at + step + options.length) % options.length];

        onChange(next.value);
        box.current?.querySelectorAll('button')[options.indexOf(next)]?.focus();
      }}
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          tabIndex={tabbable && option.value === value ? 0 : -1}
          title={option.title}
          onClick={() => onChange(option.value)}
          className={cx(
            'inline-flex items-center h-[26px] rounded-control text-[13px] whitespace-nowrap',
            compact ? 'px-2' : 'px-3',
            option.value === value
              ? 'bg-surface text-ink font-semibold shadow-lift'
              : 'bg-transparent text-ink-2',
          )}
        >
          {option.label}
          {/* The default view carries a dot, so that the segment which opens at sign-in is
              visible without pressing anything. */}
          {option.marked ? (
            <span className="w-[5px] h-[5px] ml-[7px] rounded-full bg-brand" />
          ) : null}
        </button>
      ))}
    </div>
  );
}
